import cachedHttpClient from "../cached-http-client";
import { ApiResponse } from "../../types/responses/api-response";

export interface DashboardMetrics {
  overview: {
    totalUsers: number;
    totalEvents: number;
    totalParticipants: number;
    totalRevenue: number;
    activeUsers: number;
    activeEvents: number;
    newUsers: number;
    newEvents: number;
  };
  performance: {
    bounceRate: number;
    conversionRate: number;
    avgSessionDuration: number;
    pageViews: number;
    uniqueVisitors: number;
    returnVisitors: number;
  };
  engagement: {
    averageEngagementScore: number;
    totalInteractions: number;
    socialShares: number;
    feedbackCount: number;
    averageRating: number;
    retentionRate: number;
  };
  financial: {
    totalRevenue: number;
    revenueGrowth: number;
    averageRevenuePerUser: number;
    averageRevenuePerEvent: number;
    topRevenueSources: Array<{
      source: string;
      amount: number;
      percentage: number;
    }>;
  };
  trends: {
    usersTrend: Array<{ date: string; count: number }>;
    eventsTrend: Array<{ date: string; count: number }>;
    revenueTrend: Array<{ date: string; amount: number }>;
    engagementTrend: Array<{ date: string; score: number }>;
  };
  realTime: {
    onlineUsers: number;
    activeEvents: number;
    currentSessions: number;
    systemLoad: number;
    responseTime: number;
  };
}

export interface MLModel {
  id: string;
  name: string;
  type: "classification" | "regression" | "clustering" | "recommendation";
  algorithm:
    | "random_forest"
    | "neural_network"
    | "svm"
    | "kmeans"
    | "collaborative_filtering";
  status: "training" | "deployed" | "failed" | "paused";
  accuracy: number;
  features: string[];
  createdAt: string;
  updatedAt: string;
  lastTrained: string;
  performance: {
    precision: number;
    recall: number;
    f1Score: number;
    auc: number;
  };
}

export interface DashboardWidget {
  id: string;
  type: "metric" | "chart" | "table" | "gauge" | "map";
  title: string;
  description?: string;
  position: { x: number; y: number; w: number; h: number };
  config: any;
  data: any;
  refreshInterval: number;
  lastUpdated: string;
}

export interface Dashboard {
  id: string;
  name: string;
  description?: string;
  widgets: DashboardWidget[];
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  ownerId: string;
}

export interface DashboardFilters {
  dateRange?: string;
  preset?:
    | "last_7_days"
    | "last_30_days"
    | "last_90_days"
    | "last_year"
    | "custom";
  startDate?: string;
  endDate?: string;
  metrics?: string[];
  realTime?: boolean;
}

export const dashboardAnalyticsApi = {
  // Получение метрик дашборда
  getDashboardMetrics: async (
    filters: DashboardFilters = {},
  ): Promise<ApiResponse<DashboardMetrics>> => {
    return cachedHttpClient.get("/analytics/dashboard/metrics", {
      params: filters,
    });
  },

  // Получение реального времени
  getRealTimeMetrics: async (): Promise<
    ApiResponse<{
      onlineUsers: number;
      activeEvents: number;
      currentSessions: number;
      systemLoad: number;
      responseTime: number;
      lastUpdated: string;
    }>
  > => {
    return cachedHttpClient.get("/analytics/dashboard/real-time");
  },

  // Получение трендов
  getTrends: async (
    filters: DashboardFilters = {},
  ): Promise<
    ApiResponse<{
      users: Array<{ date: string; count: number }>;
      events: Array<{ date: string; count: number }>;
      revenue: Array<{ date: string; amount: number }>;
      engagement: Array<{ date: string; score: number }>;
    }>
  > => {
    return cachedHttpClient.get("/analytics/dashboard/trends", {
      params: filters,
    });
  },

  // W2-S3 (ANL-003): dashboards-CRUD и ml-models обслуживает AnalyticsMLController
  // на /analytics/ml (реальный репозиторий; раньше GET попадал в мок AnalyticsController).

  // Получение списка дашбордов
  getDashboards: async (): Promise<ApiResponse<Dashboard[]>> => {
    return cachedHttpClient.get("/analytics/ml/dashboards");
  },

  // Создание дашборда
  createDashboard: async (data: {
    name: string;
    description?: string;
    isPublic?: boolean;
  }): Promise<ApiResponse<Dashboard>> => {
    return cachedHttpClient.post("/analytics/ml/dashboards", data);
  },

  // Обновление дашборда
  updateDashboard: async (
    id: string,
    data: Partial<Dashboard>,
  ): Promise<ApiResponse<Dashboard>> => {
    return cachedHttpClient.put(`/analytics/ml/dashboards/${id}`, data);
  },

  // Удаление дашборда
  deleteDashboard: async (
    id: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return cachedHttpClient.delete(`/analytics/ml/dashboards/${id}`);
  },

  // Получение ML моделей
  getMLModels: async (): Promise<ApiResponse<MLModel[]>> => {
    return cachedHttpClient.get("/analytics/ml/ml-models");
  },

  // Создание ML модели
  createMLModel: async (data: {
    name: string;
    type: string;
    algorithm: string;
    features: string[];
  }): Promise<ApiResponse<MLModel>> => {
    return cachedHttpClient.post("/analytics/ml/ml-models", data);
  },

  // Обучение ML модели
  trainMLModel: async (
    id: string,
  ): Promise<
    ApiResponse<{
      modelId: string;
      status: string;
      progress: number;
      estimatedTime: number;
    }>
  > => {
    return cachedHttpClient.post(`/analytics/ml/ml-models/${id}/train`);
  },

  // Получение статуса обучения
  getTrainingStatus: async (
    id: string,
  ): Promise<
    ApiResponse<{
      status: string;
      progress: number;
      accuracy: number;
      loss: number;
      estimatedTime: number;
    }>
  > => {
    return cachedHttpClient.get(
      `/analytics/ml/ml-models/${id}/training-status`,
    );
  },

  // Генерация отчета
  generateReport: async (data: {
    type: "pdf" | "excel" | "csv";
    format: "summary" | "detailed" | "custom";
    dateRange: string;
    includeCharts: boolean;
    includeMLInsights: boolean;
  }): Promise<
    ApiResponse<{
      reportId: string;
      downloadUrl: string;
      filename: string;
      expiresAt: string;
    }>
  > => {
    return cachedHttpClient.post("/analytics/reports/generate", data);
  },

  // Получение статуса отчета
  getReportStatus: async (
    reportId: string,
  ): Promise<
    ApiResponse<{
      status: "pending" | "generating" | "completed" | "failed";
      progress: number;
      downloadUrl?: string;
      error?: string;
    }>
  > => {
    return cachedHttpClient.get(`/analytics/reports/${reportId}/status`);
  },
};

export default dashboardAnalyticsApi;
